import React, { Suspense, useEffect, useState } from 'react';
import classNames from 'classnames';
import { SuccessToastContext } from 'contexts/SuccessToastContext';
import Cookie from 'js-cookie';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import FullScreenSpinner from 'Components/FullScreenSpinner';
import MaintenancePage from 'Components/Maintenance';
import { OneGraphResumeSaveSuccessToast } from 'Components/Notifications/Notifications';
import OneGraphProvider from 'Components/OneGraphProvider';
import SyncAlert from 'Components/SyncAlert/SyncAlert';
import FormValuesMigrator from 'Utils/FormValuesMigrator';
import InitialPageUtil from 'Utils/InitialPageUtil';
import { retryableLazy } from 'Utils/LazyImport';
import LocalStorageManagerV1 from 'Utils/LocalStorageManagerV1';
import RemoteSyncUtil from 'Utils/RemoteSyncUtil';
import StartPage from 'Views/StartPage';
import ForceLoginPage from './views/ForceLoginPage/ForceLoginPage';
import './App.scss';
if (window.initialData.env === 'prod') {
    datadogRum.init({
        applicationId: 'e3c6adff-c964-47dc-9c20-bd3131baa274',
        clientToken: 'pub4815546462f959f02360c4d4a82e44d3',
        site: 'datadoghq.com',
        service: 'cmi-universal-resume',
        env: window.initialData.env,
        version: window.initialData.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: false,
        allowedTracingUrls: [/^https?:\/\/(.*)?.indeed.com/]
    });
}
const syncToRemote = async () => {
    if (InitialPageUtil.isInMaintenance()) {
        return;
    }
    if (window.passportInfo.isLoggedIn) {
        await RemoteSyncUtil.instance().sync();
    }
};
const initStorage = async () => {
    await FormValuesMigrator.migrateDataFromLocalToNativeStorage();
    await FormValuesMigrator.migrateToAccountNamespace();
    await LocalStorageManagerV1.instance().ready();
    await syncToRemote();
};
const initStoragePromise = initStorage();
const withStorage = async (promise) => {
    await initStoragePromise;
    return promise;
};
const getInitialPath = () => {
    if (InitialPageUtil.shouldSkipStartPage()) {
        return '/home';
    }
    return '/start';
};
const HomePage = retryableLazy(() => withStorage(import('Views/HomePage')));
const ProfilePage = retryableLazy(() => withStorage(import('Views/ProfilePage')));
const MyPage = retryableLazy(() => withStorage(import('Views/MyPage')));
const ProfileWizard = retryableLazy(() => withStorage(import('Views/ProfileWizard')));
const FullTimeResumeWizard = retryableLazy(() => withStorage(import('Views/FullTimeResumeWizard')));
const PartTimeResumeWizard = retryableLazy(() => withStorage(import('Views/PartTimeResumeWizard')));
const Print = retryableLazy(() => withStorage(import('Views/Print')));
const IndeedUsage = retryableLazy(() => import('Views/IndeedUsagePage'));
const ResumeListPage = retryableLazy(() => import('Views/ResumeListPage'));
const CTK = Cookie.get('CTK') || '';
const App = () => {
    const [isSuccessToastVisible, setIsSuccessToastVisible] = useState(false);
    const history = useHistory();
    const isInMaintenance = InitialPageUtil.isInMaintenance();
    useEffect(() => {
        // It might be problem if users are triggering sync to frequent,
        // however, the issue is unlikely to be very big and will cost too much
        // effort to solve now. Will left it to the future.
        history.listen(() => syncToRemote());
    }, [history]);
    useEffect(() => {
        if (isInMaintenance && history.location.pathname !== '/maintenance') {
            history.push('/maintenance');
        }
    }, [isInMaintenance, history]);
    return (React.createElement(OneGraphProvider, { oneGraphApiUrl: window.initialData.oneGraphApiUrl, oneGraphApiKey: window.initialData.oneGraphApiKey, countryCode: "JP", ctk: CTK, prforceGroups: window.isPrivileged ? Cookie.get('prforceGroups') : undefined },
        React.createElement(SuccessToastContext.Provider, { value: { isSuccessToastVisible, setIsSuccessToastVisible } },
            React.createElement("main", { className: classNames([
                    'App',
                    { 'App--reactnative': !!window.ReactNativeWebView }
                ]) },
                React.createElement(Suspense, { fallback: React.createElement(FullScreenSpinner, null) },
                    React.createElement(Route, { path: "/start", component: StartPage }),
                    React.createElement(Route, { path: "/home", component: HomePage }),
                    React.createElement(Route, { path: "/resumelist", component: ResumeListPage }),
                    React.createElement(Route, { path: "/profile", component: ProfilePage, exact: true }),
                    React.createElement(Route, { path: "/profileCreate", component: ProfileWizard }),
                    React.createElement(Route, { path: "/mypage", component: MyPage }),
                    React.createElement(Route, { path: "/parttimeCreate/:resumeId", component: PartTimeResumeWizard }),
                    React.createElement(Route, { path: "/fulltimeCreate/:resumeId/:resumeType", component: FullTimeResumeWizard }),
                    React.createElement(Route, { path: "/print", component: Print }),
                    React.createElement(Route, { path: "/indeedusage", component: IndeedUsage }),
                    React.createElement(Route, { path: "/forcelogin", component: ForceLoginPage, exact: true }),
                    React.createElement(Route, { path: "/maintenance", component: MaintenancePage, exact: true }),
                    React.createElement(Route, { exact: true, path: "/" },
                        React.createElement(Redirect, { to: getInitialPath() })),
                    React.createElement(SyncAlert, { alertType: "resume" }),
                    isSuccessToastVisible && React.createElement(OneGraphResumeSaveSuccessToast, null))))));
};
export default App;
